import { ApiError } from '@/lib/api/api-client'
import {
  captureIdentityScope,
  captureRequestScope,
} from '@/lib/api/request-scope'
import {
  isDeletedAccount,
  subscribeDeletedAccounts,
} from '@/lib/auth/deleted-account-state'
import { useAuth } from '@/store/auth.store'

import {
  createOwnedRestore,
  OfflineLedgerOwnerRemovedError,
} from '@scrambl/shared/offline'

import type { OfflineRunnerContext } from './offline-queue'
import { createLedgerSnapshotStorage } from './ledger-snapshot-storage'
import { createOwnedOfflineLedger, createSerialGate } from './offline-queue'
import { captureIntentOwner } from './owned-intent-store'
import { createOwnedGate } from './owned-work'
import { preserveLedgerForReload } from './reload-recovery'

const legacyKey = 'scrambl-offline-write-ledger-v1'
export const ledgerSnapshotStorage = createLedgerSnapshotStorage({
  legacyKey,
  legacy: {
    getItem: async key => window.localStorage.getItem(key),
    setItem: async (key, value) => {
      window.localStorage.setItem(key, value)
    },
    removeItem: async key => {
      window.localStorage.removeItem(key)
    },
  },
  captureOwner: captureIntentOwner,
  isDeleted: isDeletedAccount,
  onRetireOwner: owner => persistence.retireOwner(owner),
})
const persistence = createOwnedOfflineLedger(ledgerSnapshotStorage, legacyKey)
export const offlineLedger = persistence.ledger
export const flushOfflineLedger = (ownerId = useAuth.getState().user?.id) =>
  persistence.flush(ownerId)
export const forgetOfflineLedgerOwner = persistence.forgetOwner
export const forgetOfflineLedgerDevice = (
  ownerId: string,
  operationId: string,
) => ledgerSnapshotStorage.forgetOwner(ownerId, operationId)
subscribeDeletedAccounts(() => {
  const owners = new Set(
    offlineLedger.list().map(entry => entry.envelope.ownerId),
  )
  for (const owner of owners)
    if (owner && isDeletedAccount(owner))
      void persistence
        .forgetOwner(owner)
        .catch(() =>
          console.warn('[OfflineQueue] Account cleanup requires retry'),
        )
})
export const restoreOfflineLedger = createOwnedRestore({
  capture: captureIdentityScope,
  ownerId: () => useAuth.getState().user?.id ?? null,
  restore: async owner => {
    const scope = captureIdentityScope()
    try {
      await persistence.restore(owner)
    } catch (error) {
      scope.assertCurrent()
      // A verified fresh restore may discover that another window retired its
      // previous storage binding. Retry once after retiring that old memory.
      if (!(error instanceof OfflineLedgerOwnerRemovedError)) throw error
      await persistence.restore(owner)
      scope.assertCurrent()
    }
  },
})

// Keep each account's pending work while only restoring its own outbox.
// Optional call: tests mock the auth store as a plain function with no
// subscribe, and importing this module must not take the suite down.
useAuth.subscribe?.((state, previous) => {
  if (state.user?.id !== previous.user?.id) {
    void restoreOfflineLedger().catch(() =>
      console.warn('[OfflineQueue] Restore could not be confirmed'),
    )
  }
})

export function getErrorStatus(error: unknown): number | undefined {
  if (error instanceof ApiError) return error.status
  const status = (error as { status?: unknown } | null)?.status
  return typeof status === 'number' ? status : undefined
}

export const offlineRunnerContext: OfflineRunnerContext = {
  captureSession: captureRequestScope,
  flush: flushOfflineLedger,
  getOwnerId: () => useAuth.getState().user?.id ?? null,
  ledger: offlineLedger,
  gate: createOwnedGate(createSerialGate(), captureRequestScope),
  getStatus: getErrorStatus,
}

export const preserveOfflineWorkForReload = (assertCurrent: () => void) =>
  preserveLedgerForReload({
    ledger: offlineLedger,
    storage: ledgerSnapshotStorage,
    flush: () => persistence.flush(),
    assertCurrent,
  })
